import { text, list, unwrap, markerId, isEventMarker, normalizeDay } from './markerUtils';
import {
  BASE_COLUMNS, EVENT_COLUMNS, CLUB_COLUMNS, WEEKDAY_KEYS, artistColumns, TEMPLATE_ARTIST_SLOTS,
} from './markerCsvFormat';

// Writes markers in the shape of the CSV import: the technical column names of
// the xlsx templates in the header, events and clubs mixed, one row each. A
// downloaded file goes back in through "CSV-Import" as it is, so the admin can
// fix data in a spreadsheet and re-import it.
//
// Values are written as stored. The export only encodes structure (genres
// joined with "|", acts spread over artistN columns, opening hours over the
// weekday columns) and never cleans a value: a stray "@" or an odd dress code
// is exactly what a data-quality pass needs to see. The `id` column is extra:
// on re-import a row with the id of an existing marker updates that marker.

const artistsOf = (marker) => {
  const raw = unwrap(marker?.artists);
  return Array.isArray(raw) ? raw : [];
};

/** { monday: { open, close }, … } with the stored time strings, days without both times left out. */
const openingByDay = (marker) => {
  const out = {};
  const raw = unwrap(marker?.weekdays);
  if (!Array.isArray(raw)) return out;
  raw.forEach((entry) => {
    const day = normalizeDay(entry?.day);
    const open = text(entry?.open);
    const close = text(entry?.close);
    if (day && open && close) out[day.toLowerCase()] = { open, close };
  });
  return out;
};

const rowOf = (marker) => {
  const event = isEventMarker(marker);
  const row = { id: text(markerId(marker)), type: event ? 'Event' : 'Club' };
  BASE_COLUMNS.forEach((column) => { row[column] = text(marker?.[column]); });
  row.musicGenre = list(marker?.musicGenre).join('|');
  row.type = event ? 'Event' : 'Club';

  if (event) {
    EVENT_COLUMNS.forEach((column) => { row[column] = text(marker?.[column]); });
    artistsOf(marker).forEach((artist, i) => {
      row[`artist${i + 1}Name`] = text(artist?.name);
      row[`artist${i + 1}Instagram`] = text(artist?.instagram);
      row[`artist${i + 1}SpotifyId`] = text(artist?.artistSpotifyId);
    });
  } else {
    const opening = openingByDay(marker);
    WEEKDAY_KEYS.forEach((day) => {
      row[`${day}Open`] = opening[day]?.open ?? '';
      row[`${day}Close`] = opening[day]?.close ?? '';
    });
  }
  return row;
};

const quote = (value) => {
  const s = String(value ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The header the export writes for these markers; widens the act columns to the longest line-up. */
export const exportColumns = (markers) => {
  const slots = markers.reduce((max, m) => Math.max(max, artistsOf(m).length), TEMPLATE_ARTIST_SLOTS);
  return ['id', ...BASE_COLUMNS, ...EVENT_COLUMNS, ...artistColumns(slots), ...CLUB_COLUMNS];
};

/**
 * The CSV text: UTF-8 with a byte order mark (so Excel decodes umlauts), comma
 * separated, CRLF line ends, a header row and one row per marker.
 */
export function markersToCsv(markers) {
  const columns = exportColumns(markers);
  const lines = [columns.map(quote).join(',')];
  markers.forEach((marker) => {
    const row = rowOf(marker);
    lines.push(columns.map((column) => quote(row[column] ?? '')).join(','));
  });
  return `﻿${lines.join('\r\n')}\r\n`;
}

const pad = (n) => String(n).padStart(2, '0');

export const exportFilename = (date = new Date()) =>
  `marker-export-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.csv`;

/** Builds the CSV and hands it to the browser as a download. Returns the file name. */
export function downloadMarkersCsv(markers, filename = exportFilename()) {
  const blob = new Blob([markersToCsv(markers)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox starts the download after click returns; revoking right away can cancel it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}
